import React, { useState, useEffect, useRef } from 'react';
import {
  X, Search, Download, Printer, FileText, Calendar,
  Truck, User, Package, MapPin, Filter, RefreshCw,
  ChevronRight, BarChart3, AlertCircle
} from 'lucide-react';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────
   Utility: status colour map
───────────────────────────────────────────── */
function statusStyle(status) {
  const map = {
    'Pending':    { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
    'In Transit': { bg: '#dbeafe', text: '#1e3a8a', border: '#93c5fd' },
    'Delivered':  { bg: '#dcfce7', text: '#14532d', border: '#86efac' },
    'Returned':   { bg: '#fee2e2', text: '#7f1d1d', border: '#fca5a5' },
  };
  return map[status] || { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' };
}

/* ─────────────────────────────────────────────
   Session helper
───────────────────────────────────────────── */
function getSessionData() {
  try {
    const s = localStorage.getItem('ltms_session');
    if (!s) return null;
    const parsed = JSON.parse(s);
    return parsed?.session || parsed;
  } catch { return null; }
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function ManifestReport({ onClose }) {
  const navigate  = useNavigate();
  const printRef  = useRef(null);
  const session   = getSessionData();

  const today = new Date().toISOString().slice(0, 10);

  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [manifestData,     setManifestData]     = useState(null);
  const [vehicles,         setVehicles]         = useState([]);
  const [drivers,          setDrivers]          = useState([]);
  const [cities,           setCities]           = useState([]);
  const [courierStatuses,  setCourierStatuses]  = useState([]);
  const [truckAssignments, setTruckAssignments] = useState([]);

  const [filters, setFilters] = useState({
    StartDate:        today,
    EndDate:          today,
    VehicleID:        '',
    DriverID:         '',
    OriginCityID:     '',
    DestinationCityID:'',
    Status:           '',
    ConsignmentType:  'LT',
    TruckAssignmentID:'',
  });

  /* ── Load dropdowns ── */
  useEffect(() => {
    (async () => {
      try {
        const [veh, drv, cty, sta, asg] = await Promise.all([
          apiClient.get('/Vehicles.php').catch(()=>({data:{success:false}})),
          apiClient.get('/Drivers.php').catch(()=>({data:{success:false}})),
          apiClient.get('/Cities.php').catch(()=>({data:{success:false}})),
          apiClient.get('/CourierStatus.php').catch(()=>({data:{success:false}})),
          apiClient.get('/VehicleAssignments.php').catch(()=>({data:{success:false}})),
        ]);
        if (veh.data.success) setVehicles(veh.data.data || []);
        if (drv.data.success) setDrivers((drv.data.data||[]).filter(d=>parseInt(d.isactive)===1));
        if (cty.data.success) setCities(cty.data.data || []);
        if (sta.data.success) setCourierStatuses(sta.data.data || []);
        if (asg.data.success) setTruckAssignments(asg.data.data || []);
      } catch(e) { console.error('Dropdown load error', e); }
    })();
  }, []);

  /* ── Handlers ── */
  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(p => ({ ...p, [name]: value }));
  };

  const handleGenerateReport = async () => {
    if (!filters.StartDate || !filters.EndDate) { setError('Please select start and end dates'); return; }
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/ManifestReport.php', { params: filters });
      if (res.data.success) setManifestData(res.data.data);
      else setError(res.data.message || 'Failed to generate report');
    } catch(e) {
      setError('Error: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  };

  const handlePrint = () => {
    const orig = document.title;
    const f = filters.StartDate.split('-').reverse().join('-');
    const t = filters.EndDate.split('-').reverse().join('-');
    document.title = `Manifest_${f}_to_${t}`;
    window.print();
    setTimeout(() => { document.title = orig; }, 200);
  };

  const handleExportCSV = () => {
    const rows = manifestData?.consignments;
    if (!rows?.length) { alert('No data to export'); return; }
    const headers = ['Consignment No','Date','Shipper','Consignee','Origin','Destination','Weight(kg)','Packages','Vehicle','Driver','Status'];
    const lines = rows.map(c => [
      c.ConsignmentNo,'',
      new Date(c.ConsignmentDate).toLocaleDateString(),
      c.ShipperName||'', c.ConsigneeName||'',
      c.OriginCity||'', c.DestinationCity||'',
      c.Weight||0, c.NoOfPkgs||0,
      c.VehicleNo||'', c.DriverName||'', c.Status||''
    ].map(v=>`"${v}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type:'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `manifest_${filters.StartDate}_${filters.EndDate}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleClear = () => {
    setFilters({ StartDate:today, EndDate:today, VehicleID:'', DriverID:'',
      OriginCityID:'', DestinationCityID:'', Status:'', ConsignmentType:'LT', TruckAssignmentID:'' });
    setManifestData(null); setError('');
  };

  const handleClose = () => { if (onClose) onClose(); else navigate('/dashboard'); };

  /* ─────────────────────────────────────── Session guard */
  if (!session?.companyid) {
    return (
      <div style={S.overlay} onClick={e => e.target===e.currentTarget && handleClose()}>
        <div style={{...S.modal, maxWidth:480, padding:32, textAlign:'center'}}>
          <AlertCircle size={48} style={{color:'#ef4444', marginBottom:16}} />
          <h3 style={{margin:'0 0 8px',color:'#1e293b'}}>Session Required</h3>
          <p style={{color:'#64748b'}}>Please select a company and financial year from the dashboard.</p>
          <button style={S.btnPrimary} onClick={handleClose}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────── Derived values */
  const consignments   = manifestData?.consignments || [];
  const summary        = manifestData?.summary || {};
  const company        = manifestData?.company || {};
  const totalWeight    = consignments.reduce((s,c)=>s+parseFloat(c.Weight||0),0).toFixed(2);
  const totalPkgs      = consignments.reduce((s,c)=>s+parseInt(c.NoOfPkgs||0),0);
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB') : '—';
  const periodLabel = `${fmtDate(filters.StartDate)} – ${fmtDate(filters.EndDate)}`;

  /* ═══════════════════════════════════════ RENDER */
  return (
    <>
      {/* ───────── INJECTED STYLES ───────── */}
      <style>{STYLES}</style>

      <div className="mr-overlay" onClick={e => e.target===e.currentTarget && handleClose()}>
        <div className="mr-modal" onClick={e => e.stopPropagation()}>

          {/* ══ SCREEN HEADER ══ */}
          <header className="mr-header no-print">
            <div className="mr-header-left">
              <div className="mr-header-icon"><FileText size={26} /></div>
              <div>
                <div className="mr-header-title">Manifest Report</div>
                <div className="mr-header-sub">Land Transport · {periodLabel}</div>
              </div>
            </div>
            <button className="mr-close-btn" onClick={handleClose} title="Close"><X size={20}/></button>
          </header>

          {/* ══ TOOLBAR ══ */}
          <div className="mr-toolbar no-print">
            <button className="mr-btn mr-btn-primary" onClick={handleGenerateReport} disabled={loading}>
              <Search size={16}/> Generate
            </button>
            <button className="mr-btn" onClick={handlePrint} disabled={!manifestData||loading}>
              <Printer size={16}/> Print
            </button>
            <button className="mr-btn" onClick={handleExportCSV} disabled={!manifestData||loading}>
              <Download size={16}/> CSV
            </button>
            <button className="mr-btn mr-btn-ghost" onClick={handleClear} disabled={loading}>
              <RefreshCw size={16}/> Clear
            </button>
          </div>

          {/* ══ ERROR ══ */}
          {error && (
            <div className="mr-error no-print">
              <AlertCircle size={16}/> {error}
            </div>
          )}

          {/* ══ SCROLLABLE BODY ══ */}
          <div className="mr-body">

            {/* ── FILTERS ── */}
            <section className="mr-section no-print">
              <div className="mr-section-title"><Filter size={15}/> Report Filters</div>
              <div className="mr-grid">
                <Field label="Start Date *">
                  <input type="date" name="StartDate" value={filters.StartDate} onChange={handleFilterChange} className="mr-input"/>
                </Field>
                <Field label="End Date *">
                  <input type="date" name="EndDate" value={filters.EndDate} onChange={handleFilterChange} className="mr-input"/>
                </Field>
                <Field label="Type">
                  <select name="ConsignmentType" value={filters.ConsignmentType} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Types</option>
                    <option value="LT">Land Transport (LT)</option>
                    <option value="AT">Air Transport (AT)</option>
                    <option value="ST">Sea Transport (ST)</option>
                  </select>
                </Field>
                <Field label="Vehicle">
                  <select name="VehicleID" value={filters.VehicleID} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Vehicles</option>
                    {vehicles.map(v=><option key={v.VehicleID} value={v.VehicleID}>{v.RegistrationNo}{v.VehicleType&&` – ${v.VehicleType}`}</option>)}
                  </select>
                </Field>
                <Field label="Driver">
                  <select name="DriverID" value={filters.DriverID} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Drivers</option>
                    {drivers.map(d=><option key={d.driverid} value={d.driverid}>{d.drivername}</option>)}
                  </select>
                </Field>
                <Field label="Origin City">
                  <select name="OriginCityID" value={filters.OriginCityID} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Origins</option>
                    {cities.map(c=><option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                  </select>
                </Field>
                <Field label="Destination City">
                  <select name="DestinationCityID" value={filters.DestinationCityID} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Destinations</option>
                    {cities.map(c=><option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select name="Status" value={filters.Status} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Statuses</option>
                    {courierStatuses.map(s=><option key={s.CourierStatusID} value={s.CourierStatusID}>{s.StatusName}</option>)}
                  </select>
                </Field>
                <Field label="Truck Assignment">
                  <select name="TruckAssignmentID" value={filters.TruckAssignmentID} onChange={handleFilterChange} className="mr-input">
                    <option value="">All Assignments</option>
                    {truckAssignments.map(a=>{
                      const d=a.AssignmentDate?new Date(a.AssignmentDate).toLocaleDateString():'';
                      const label=[
                        a.TripDocNo||`TD-${String(a.AssignmentID).padStart(6,'0')}`,
                        d, a.OriginCity||a.Origin||'',
                        a.DestinationCity||a.Destination||'',
                        a.DriverName||''
                      ].filter(Boolean).join(' | ');
                      return <option key={a.AssignmentID} value={a.AssignmentID}>{label}</option>;
                    })}
                  </select>
                </Field>
              </div>
            </section>

            {/* ── LOADING ── */}
            {loading && (
              <div className="mr-loading no-print">
                <div className="mr-spinner"/>
                <span>Generating manifest report…</span>
              </div>
            )}

            {/* ══════════════════════════════════════════
                PRINTABLE REPORT  (visible on screen too)
            ══════════════════════════════════════════ */}
            {manifestData && !loading && (
              <div ref={printRef} className="mr-report">

                {/* ── PRINT LETTERHEAD ── */}
                <div className="mr-letterhead">
                  <div className="mr-lh-company">{company.CompanyName || 'Company Name'}</div>
                  {company.Address && <div className="mr-lh-addr">{company.Address}</div>}
                  {(company.Phone||company.Email) && (
                    <div className="mr-lh-contact">
                      {company.Phone && <span>Tel: {company.Phone}</span>}
                      {company.Email && <span>Email: {company.Email}</span>}
                    </div>
                  )}
                  <div className="mr-lh-divider"/>
                  <div className="mr-lh-report-name">MANIFEST REPORT — LAND TRANSPORT</div>
                  <div className="mr-lh-meta">
                    <span><strong>Period:</strong> {fmtDate(filters.StartDate)} to {fmtDate(filters.EndDate)}</span>
                    <span><strong>Generated:</strong> {new Date().toLocaleString('en-GB')}</span>
                    {filters.ConsignmentType && <span><strong>Type:</strong> {filters.ConsignmentType}</span>}
                  </div>
                </div>

                {/* ── SUMMARY CARDS ── */}
                <div className="mr-summary">
                  <SummaryCard color="#1e40af" bg="#eff6ff" label="Total Consignments" value={summary.totalConsignments??0} />
                  <SummaryCard color="#065f46" bg="#f0fdf4" label="Total Weight (kg)"  value={parseFloat(summary.totalWeight??0).toFixed(2)} />
                  <SummaryCard color="#92400e" bg="#fef3c7" label="Total Packages"     value={summary.totalPackages??0} />
                  <SummaryCard color="#6b21a8" bg="#faf5ff" label="Vehicles Used"      value={summary.vehiclesUsed??0} />
                </div>

                {/* ── APPLIED FILTERS BADGE ROW (screen) ── */}
                {(filters.VehicleID||filters.DriverID||filters.OriginCityID||filters.DestinationCityID||filters.TruckAssignmentID) && (
                  <div className="mr-filter-badges no-print">
                    <span className="mr-fb-label">Filters:</span>
                    {filters.VehicleID        && <Badge>Vehicle: {vehicles.find(v=>v.VehicleID===filters.VehicleID)?.RegistrationNo}</Badge>}
                    {filters.DriverID         && <Badge>Driver: {drivers.find(d=>d.driverid===filters.DriverID)?.drivername}</Badge>}
                    {filters.OriginCityID     && <Badge>Origin: {cities.find(c=>c.cityid===filters.OriginCityID)?.cityname}</Badge>}
                    {filters.DestinationCityID && <Badge>Dest: {cities.find(c=>c.cityid===filters.DestinationCityID)?.cityname}</Badge>}
                    {filters.TruckAssignmentID && <Badge>Trip: {truckAssignments.find(a=>String(a.AssignmentID)===String(filters.TruckAssignmentID))?.TripDocNo||`TD-${String(filters.TruckAssignmentID).padStart(6,'0')}`}</Badge>}
                  </div>
                )}

                {/* ── CONSIGNMENTS TABLE ── */}
                <div className="mr-table-wrap">
                  <div className="mr-table-heading">
                    <Package size={15}/> Consignment Details
                    <span className="mr-table-count">{consignments.length} record{consignments.length!==1?'s':''}</span>
                  </div>

                  {consignments.length > 0 ? (
                    <table className="mr-table">
                      <thead>
                        <tr>
                          <th style={{width:28}}>#</th>
                          <th style={{width:110}}>Consignment No</th>
                          <th style={{width:78}}>Date</th>
                          <th>Shipper</th>
                          <th>Consignee</th>
                          <th style={{width:130}}>Route</th>
                          <th style={{width:72,textAlign:'right'}}>Wt (kg)</th>
                          <th style={{width:48,textAlign:'right'}}>Pkgs</th>
                          <th style={{width:95}}>Vehicle</th>
                          <th style={{width:110}}>Driver</th>
                          <th style={{width:85}}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consignments.map((c,i)=>{
                          const ss = statusStyle(c.Status);
                          return (
                            <tr key={c.ConsignmentID||i} className={i%2===0?'mr-row-even':'mr-row-odd'}>
                              <td style={{color:'#94a3b8',textAlign:'center'}}>{i+1}</td>
                              <td style={{fontFamily:'monospace',fontWeight:700,fontSize:11}}>{c.ConsignmentNo}</td>
                              <td style={{fontSize:11}}>{fmtDate(c.ConsignmentDate)}</td>
                              <td>
                                <div className="mr-cell-main">{c.ShipperName||'—'}</div>
                                {c.ShipperPhone && <div className="mr-cell-sub">{c.ShipperPhone}</div>}
                              </td>
                              <td>
                                <div className="mr-cell-main">{c.ConsigneeName||'—'}</div>
                                {c.ConsigneePhone && <div className="mr-cell-sub">{c.ConsigneePhone}</div>}
                              </td>
                              <td style={{fontSize:11}}>
                                <span className="mr-route">
                                  {c.OriginCity||'N/A'}
                                  <ChevronRight size={10} style={{margin:'0 1px',opacity:.5}}/>
                                  {c.DestinationCity||'N/A'}
                                </span>
                              </td>
                              <td style={{textAlign:'right',fontWeight:600,fontSize:11}}>{parseFloat(c.Weight||0).toFixed(2)}</td>
                              <td style={{textAlign:'right',fontSize:11}}>{c.NoOfPkgs||0}</td>
                              <td style={{fontSize:11}}>{c.VehicleNo||'—'}</td>
                              <td style={{fontSize:11}}>{c.DriverName||'—'}</td>
                              <td>
                                <span className="mr-status-badge" style={{background:ss.bg,color:ss.text,border:`1px solid ${ss.border}`}}>
                                  {c.Status||'Pending'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="mr-totals-row">
                          <td colSpan={6} style={{textAlign:'right',fontWeight:700}}>TOTALS</td>
                          <td style={{textAlign:'right',fontWeight:700}}>{totalWeight}</td>
                          <td style={{textAlign:'right',fontWeight:700}}>{totalPkgs}</td>
                          <td colSpan={3}/>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div className="mr-empty">
                      <Package size={40} style={{opacity:.3,marginBottom:12}}/>
                      <p>No consignments found for the selected filters</p>
                    </div>
                  )}
                </div>

                {/* ── SIGNATURE BLOCK (print only) ── */}
                <div className="mr-signatures print-only">
                  {['Prepared By','Checked By','Approved By'].map(l=>(
                    <div key={l} className="mr-sig-box">
                      <div className="mr-sig-line"/>
                      <div className="mr-sig-label">{l}</div>
                      <div className="mr-sig-name">Name: _______________</div>
                      <div className="mr-sig-name">Date: _______________</div>
                    </div>
                  ))}
                </div>

                {/* ── PRINT FOOTER ── */}
                <div className="mr-print-footer print-only">
                  <span>This is a computer-generated manifest report.</span>
                  <span>Page 1</span>
                </div>

              </div>
            )}
          </div>{/* /mr-body */}
        </div>
      </div>
    </>
  );
}

/* ─── Small helper components ─── */
function Field({ label, children }) {
  return (
    <div className="mr-field">
      <label className="mr-label">{label}</label>
      {children}
    </div>
  );
}
function SummaryCard({ label, value, bg, color }) {
  return (
    <div className="mr-card" style={{background:bg,borderColor:color+'33'}}>
      <div className="mr-card-label" style={{color}}>{label}</div>
      <div className="mr-card-value" style={{color}}>{value}</div>
    </div>
  );
}
function Badge({ children }) {
  return <span className="mr-badge">{children}</span>;
}

/* ─── Inline style object (for session guard only) ─── */
const S = {
  overlay:   { position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 },
  modal:     { background:'#fff',borderRadius:12,padding:32,boxShadow:'0 20px 60px rgba(0,0,0,0.2)',maxWidth:480,width:'90%' },
  btnPrimary:{ marginTop:16,padding:'10px 24px',background:'#4f46e5',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:14 },
};

/* ═══════════════════════════════════════════════════════════
   ALL CSS — SCREEN + PRINT
═══════════════════════════════════════════════════════════ */
const STYLES = `
/* ── Reset inside component ── */
.mr-overlay {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.6);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 16px;
}
.mr-modal {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.25);
  display: flex; flex-direction: column;
  width: 100%; max-width: 1100px;
  max-height: 95vh;
  overflow: hidden;
}

/* ── Header ── */
.mr-header {
  background: linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%);
  padding: 18px 24px;
  display: flex; align-items: center; justify-content: space-between;
  border-radius: 14px 14px 0 0; flex-shrink: 0;
}
.mr-header-left { display:flex; align-items:center; gap:14px; }
.mr-header-icon {
  width:46px; height:46px; border-radius:10px;
  background:rgba(255,255,255,0.2);
  display:flex; align-items:center; justify-content:center;
  color:#fff; flex-shrink:0;
}
.mr-header-title { font-size:20px; font-weight:800; color:#fff; line-height:1.2; }
.mr-header-sub   { font-size:13px; color:rgba(255,255,255,0.75); margin-top:2px; }
.mr-close-btn {
  width:38px; height:38px; border-radius:8px;
  background:rgba(255,255,255,0.15); border:1px solid rgba(255,255,255,0.25);
  color:#fff; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition: background .2s;
}
.mr-close-btn:hover { background:rgba(255,255,255,0.3); }

/* ── Toolbar ── */
.mr-toolbar {
  display:flex; gap:8px; padding:12px 16px;
  background:#f8fafc; border-bottom:1px solid #e2e8f0; flex-shrink:0;
}
.mr-btn {
  display:flex; align-items:center; gap:6px;
  padding:7px 16px; border-radius:7px; font-size:13px; font-weight:600;
  cursor:pointer; border:1px solid #e2e8f0;
  background:#fff; color:#334155; transition:all .15s;
}
.mr-btn:hover:not(:disabled) { background:#f1f5f9; border-color:#cbd5e1; }
.mr-btn:disabled { opacity:.45; cursor:not-allowed; }
.mr-btn-primary { background:#1e40af; color:#fff; border-color:#1e40af; }
.mr-btn-primary:hover:not(:disabled) { background:#1d4ed8; border-color:#1d4ed8; }
.mr-btn-ghost { background:transparent; border-color:transparent; color:#64748b; }
.mr-btn-ghost:hover:not(:disabled) { background:#f1f5f9; border-color:#e2e8f0; }

/* ── Error ── */
.mr-error {
  margin:12px 16px 0; padding:10px 14px; border-radius:7px;
  background:#fef2f2; border:1px solid #fecaca; color:#b91c1c;
  font-size:13px; display:flex; align-items:center; gap:8px; flex-shrink:0;
}

/* ── Body / scroll area ── */
.mr-body {
  flex:1; overflow-y:auto; padding:16px;
}

/* ── Section ── */
.mr-section { margin-bottom:16px; }
.mr-section-title {
  display:flex; align-items:center; gap:7px;
  font-size:13px; font-weight:700; color:#374151;
  margin-bottom:12px; text-transform:uppercase; letter-spacing:.5px;
}

/* ── Grid ── */
.mr-grid {
  display:grid;
  grid-template-columns: repeat(auto-fill, minmax(200px,1fr));
  gap:12px;
}
.mr-field { display:flex; flex-direction:column; gap:4px; }
.mr-label { font-size:12px; font-weight:600; color:#475569; }
.mr-input {
  padding:7px 10px; border-radius:7px; border:1px solid #d1d5db;
  font-size:13px; color:#1e293b; background:#fff;
  outline:none; transition:border .15s; width:100%; box-sizing:border-box;
}
.mr-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.15); }

/* ── Loading ── */
.mr-loading {
  display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:12px; padding:48px;
  color:#64748b; font-size:14px;
}
.mr-spinner {
  width:36px; height:36px; border-radius:50%;
  border:3px solid #e2e8f0; border-top-color:#3b82f6;
  animation:mr-spin 0.8s linear infinite;
}
@keyframes mr-spin { to { transform:rotate(360deg); } }

/* ── Report container ── */
.mr-report { display:flex; flex-direction:column; gap:16px; }

/* ── Letterhead ── */
.mr-letterhead {
  text-align:center; padding:16px 0 12px;
  border-bottom:2px solid #1e3a8a;
}
.mr-lh-company  { font-size:22px; font-weight:800; color:#1e293b; }
.mr-lh-addr     { font-size:12px; color:#64748b; margin-top:3px; }
.mr-lh-contact  { font-size:12px; color:#64748b; display:flex; justify-content:center; gap:20px; margin-top:2px; }
.mr-lh-divider  { border:none; border-top:1px solid #e2e8f0; margin:10px auto; width:80%; }
.mr-lh-report-name {
  font-size:15px; font-weight:800; color:#1e3a8a;
  letter-spacing:1px; text-transform:uppercase;
  margin-bottom:6px;
}
.mr-lh-meta {
  display:flex; justify-content:center; gap:24px;
  font-size:12px; color:#475569; flex-wrap:wrap;
}

/* ── Summary cards ── */
.mr-summary {
  display:grid; grid-template-columns:repeat(4,1fr); gap:12px;
}
.mr-card {
  padding:14px 16px; border-radius:10px;
  border:1px solid transparent;
}
.mr-card-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; margin-bottom:6px; }
.mr-card-value { font-size:26px; font-weight:800; }

/* ── Filter badges ── */
.mr-filter-badges {
  display:flex; flex-wrap:wrap; gap:6px; align-items:center;
  padding:8px 12px; background:#f8fafc; border-radius:8px; border:1px solid #e2e8f0;
}
.mr-fb-label { font-size:12px; font-weight:600; color:#64748b; }
.mr-badge {
  padding:3px 10px; background:#e0e7ff; color:#3730a3;
  border-radius:100px; font-size:11px; font-weight:600;
}

/* ── Table wrapper ── */
.mr-table-wrap { }
.mr-table-heading {
  display:flex; align-items:center; gap:7px;
  font-size:13px; font-weight:700; color:#1e293b;
  margin-bottom:10px; text-transform:uppercase; letter-spacing:.5px;
}
.mr-table-count {
  margin-left:auto; font-size:12px; font-weight:500;
  color:#64748b; text-transform:none; letter-spacing:0;
  background:#f1f5f9; padding:2px 10px; border-radius:100px;
}

/* ── Table ── */
.mr-table {
  width:100%; border-collapse:collapse;
  font-size:12px; color:#1e293b;
}
.mr-table th {
  background:#1e3a8a; color:#fff;
  padding:9px 8px; text-align:left;
  font-size:11px; font-weight:700; letter-spacing:.3px;
  border:1px solid #1e40af;
}
.mr-table td { padding:7px 8px; border:1px solid #e2e8f0; vertical-align:middle; }
.mr-row-even { background:#fff; }
.mr-row-odd  { background:#f8fafc; }
.mr-table tbody tr:hover { background:#eff6ff; }

.mr-cell-main { font-size:11px; font-weight:600; color:#1e293b; }
.mr-cell-sub  { font-size:10px; color:#94a3b8; margin-top:1px; }
.mr-route     { font-size:11px; display:flex; align-items:center; gap:2px; color:#374151; }

.mr-status-badge {
  display:inline-block; padding:3px 8px; border-radius:5px;
  font-size:10px; font-weight:700; white-space:nowrap;
  -webkit-print-color-adjust:exact; print-color-adjust:exact;
}

/* ── Totals row ── */
.mr-totals-row {
  background:#1e3a8a !important;
  -webkit-print-color-adjust:exact; print-color-adjust:exact;
}
.mr-totals-row td {
  color:#fff !important; font-weight:700; font-size:12px;
  border-color:#1e40af !important; padding:9px 8px !important;
}

/* ── Empty state ── */
.mr-empty {
  text-align:center; padding:48px 24px;
  color:#94a3b8; display:flex; flex-direction:column; align-items:center;
}

/* ── Signature block ── */
.mr-signatures {
  display:grid; grid-template-columns:repeat(3,1fr); gap:60px;
  margin-top:48px; padding-top:16px;
}
.mr-sig-box    { text-align:center; }
.mr-sig-line   { border-top:2px solid #374151; width:80%; margin:0 auto 10px; }
.mr-sig-label  { font-weight:700; font-size:13px; color:#1e293b; margin-bottom:6px; }
.mr-sig-name   { font-size:11px; color:#64748b; margin-top:4px; }

/* ── Print footer ── */
.mr-print-footer {
  display:flex; justify-content:space-between;
  font-size:10px; color:#94a3b8;
  margin-top:24px; padding-top:8px;
  border-top:1px solid #e2e8f0;
}

/* ─── Utility ─── */
.print-only { display:none !important; }
.no-print   { /* nothing — visible on screen */ }

/* ════════════════════════════════════════════════════
   PRINT STYLES
════════════════════════════════════════════════════ */
@media print {
  /* 1. Hide screen-only chrome */
  .no-print { display:none !important; }
  .print-only { display:block !important; }

  /* 2. Flatten overlay / modal */
  .mr-overlay {
    position:static !important;
    background:transparent !important;
    backdrop-filter:none !important;
    padding:0 !important;
    display:block !important;
  }
  .mr-modal {
    max-height:none !important;
    box-shadow:none !important;
    border-radius:0 !important;
    overflow:visible !important;
    width:100% !important;
    max-width:100% !important;
    display:block !important;
  }
  .mr-body {
    overflow:visible !important;
    padding:0 !important;
  }

  /* 3. Summary cards — force flex row */
  .mr-summary {
    display:flex !important;
    flex-wrap:nowrap !important;
    gap:8px !important;
    margin-bottom:12px !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  .mr-card {
    flex:1 !important;
    padding:10px 12px !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  .mr-card-value { font-size:20px !important; }

  /* 4. Letterhead */
  .mr-letterhead { padding:8px 0 !important; }
  .mr-lh-company { font-size:18px !important; }

  /* 5. Table */
  .mr-table-wrap { overflow:visible !important; }
  .mr-table {
    font-size:9px !important;
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }
  .mr-table th,
  .mr-table td { padding:5px 4px !important; font-size:9px !important; }
  .mr-table th { background:#1e3a8a !important; color:#fff !important; }
  .mr-totals-row,
  .mr-totals-row td { background:#1e3a8a !important; color:#fff !important; }
  .mr-row-odd { background:#f8fafc !important; }
  .mr-cell-main { font-size:9px !important; }
  .mr-cell-sub  { font-size:8px !important; }
  .mr-status-badge { font-size:8px !important; padding:2px 5px !important; }

  /* 6. Signatures — grid for print */
  .mr-signatures {
    display:grid !important;
    grid-template-columns:repeat(3,1fr) !important;
    gap:40px !important;
  }

  /* 7. Print footer */
  .mr-print-footer { display:flex !important; }

  /* 8. Page setup */
  @page {
    size: A4 landscape;
    margin: 1cm 1.2cm;
  }
  body {
    -webkit-print-color-adjust:exact !important;
    print-color-adjust:exact !important;
  }

  /* 9. Avoid breaks inside rows */
  .mr-table tr { page-break-inside:avoid !important; }
  .mr-table thead { display:table-header-group !important; }
  .mr-table tfoot { display:table-footer-group !important; }
}
`;